import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import QuestionReponseForm from './questionReponseForm';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { SessionContext } from '../../services/SessionProvider';
import { getQuestionSuivateByQuestionId, getAllQuestions } from '../../services/requestHandler';
import RedemarrerQuestionnaire from './redemarrerQuestionnaire';
import LoadingForm from '../forms/loadingForm';
import translate from '../../i18n/translate';

const defaultQuestionnaire = {
  id: null,
  contenu: '',
  contenu_en: '',
  parent_des_reponses: [],
  strategies: []
};

const SafeQuestionReponseForm = ({ questionReponses, ...props }) => {
  if (!questionReponses || typeof questionReponses.contenu === 'undefined') {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        {translate("questionnaire.erreur_chargement_question") || "Question non disponible."}
      </div>
    );
  }

  return <QuestionReponseForm questionReponses={questionReponses} {...props} />;
};

export default function CheminQuestions() {
  const params = useParams();
  const navigate = useNavigate();

  const {
    retirerQuestionChemin,
    getIsQuestionnaireStarted,
    checkIfStringNeedsFormatting,
    formatString,
  } = useContext(SessionContext);

  const [isLoading, setIsLoading] = useState(true);
  const [questionnaire, setQuestionnaire] = useState(defaultQuestionnaire);
  const [strategie, setStrategie] = useState([]);
  const [stateCheminDone, setStateCheminDone] = useState(false);
  const [usedBack, setUsedBack] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  useEffect(() => {
    loadQuestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.questionId]);

  const formatQuestion = async (data) => {
    if (!data) return defaultQuestionnaire;

    const result = { ...data };

    if (checkIfStringNeedsFormatting(result.contenu)) {
      result.contenu = await formatString(result.contenu, true);
      result.contenu_en = await formatString(result.contenu_en, false);
    }

    return result;
  };

  const loadQuestion = async () => {
    setIsLoading(true);

    try {
      if (params.questionId === '0') {
        const data = await getAllQuestions();
        const firstQuestion = data?.[0] || defaultQuestionnaire;

        const formatted = await formatQuestion(firstQuestion);

        setQuestionnaire(formatted);
        setStrategie([]);
        setStateCheminDone(false);
      } else {
        const data = await getQuestionSuivateByQuestionId(params.questionId);

        if (usedBack) {
          retirerQuestionChemin();
          setUsedBack(false);
        }

        const formatted = await formatQuestion(data);
        setQuestionnaire(formatted);

        if (data?.strategies?.length > 0) {
          const formattedStrategies = await Promise.all(
            data.strategies.map((s) => formatQuestion(s))
          );
          setStrategie(formattedStrategies);
        } else {
          setStrategie([]);
        }

        setStateCheminDone(
          data?.strategies?.length === 1 &&
          data?.parent_des_reponses?.length === 0
        );
      }
    } catch (error) {
      console.error("Erreur lors du chargement de la question :", error);
      setQuestionnaire(defaultQuestionnaire);
      setStrategie([]);
      setStateCheminDone(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackBouton = () => {
    setUsedBack(true);
    navigate(-1);
  };

  const isQuestionnaireStarted = getIsQuestionnaireStarted();

  return (
    <main style={{ display: 'grid', justifyItems: 'center', marginBottom: '30px' }}>
      {isQuestionnaireStarted && !isLoading && (
        <>
          <div style={{ width: '100%' }}>
            <SafeQuestionReponseForm
              onClick={() => setIsLoading(true)}
              questionReponses={questionnaire}
              strategieReponses={strategie}
              stateCheminDone={stateCheminDone}
              needReloadComponent={() => {}}
              reloadComponent={false}
            />
          </div>

          {params.questionId !== '0' && (
            <div>
              <Button
                variant="contained"
                onClick={() => setOpenConfirmation(true)}
                sx={{
                  backgroundColor: '#307B5B',
                  '&:hover': {
                    backgroundColor: '#8EBF55',
                    color: 'black',
                    fontWeight: 'bold'
                  }
                }}
                style={{ width: '8rem', marginRight: '1rem' }}
              >
                {translate("questionnaire.recommencer")}
              </Button>

              <Button
                variant="contained"
                onClick={handleBackBouton}
                sx={{
                  backgroundColor: '#307B5B',
                  '&:hover': {
                    backgroundColor: '#8EBF55',
                    color: 'black',
                    fontWeight: 'bold'
                  }
                }}
                style={{ width: '3rem' }}
              >
                {translate("questionnaire.retour")}
              </Button>
            </div>
          )}
        </>
      )}

      {!isQuestionnaireStarted && !isLoading && <RedemarrerQuestionnaire />}

      {isQuestionnaireStarted && isLoading && (
        <div style={{ paddingTop: '25%' }}>
          <LoadingForm />
        </div>
      )}

      <Dialog open={openConfirmation} onClose={() => setOpenConfirmation(false)}>
        <DialogContent style={{ width: '25rem' }}>
          <DialogContentText>
            Êtes-vous certain de vouloir recommencer le questionnaire ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setOpenConfirmation(false);
              navigate('/questionnaire');
            }}
          >
            Oui
          </Button>
          <Button onClick={() => setOpenConfirmation(false)}>
            Non
          </Button>
        </DialogActions>
      </Dialog>
    </main>
  );
}
